// 시간표 렌더링에 필요한 공유 타입, 설정, 유틸리티

export const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type DayKey = (typeof DAY_KEYS)[number];

/**
 * 시간표 그리드 렌더링 설정.
 * SchedulePage에서 생성해 ScheduleGrid에 전달하면
 * 하위 컴포넌트까지 전파됩니다.
 */
export interface ScheduleConfig {
  /** 1시간 당 높이 (px). 기본값: 40 */
  hourHeight: number;
  /** 좌측 시간 레이블 영역 너비 (px). 기본값: 75 */
  leftMarginPx: number;
  /** 우측 여백 (px). 기본값: 30 */
  rightMarginPx: number;
  /** 요일 컬럼 수. 기본값: 7 */
  dayColumns: number;
  /** 요일 레이블 배열. DAY_KEYS 순서와 일치해야 합니다. 기본값: 한국어 요일 */
  days: string[];
  /** 수업이 없을 때 표시할 최초 시간. 기본값: 8 */
  startHour: number;
  /** 종료 시간 상한 (범위 계산용 sentinel). 기본값: 23 */
  endHour: number;
}

export const DEFAULT_SCHEDULE_CONFIG: ScheduleConfig = {
  hourHeight: 30,
  leftMarginPx: 75,
  rightMarginPx: 30,
  dayColumns: 7,
  days: ["월", "화", "수", "목", "금", "토", "일"],
  startHour: 8,
  endHour: 23,
};

export interface ClassData {
  id: number;
  className: string;
  teacher: string;
  classRoom: string | null;
  classCategory?: "regular" | "special" | "supplement";
  classStartDate?: string | null;
  classEndDate?: string | null;
  /** 수강생 이름 목록 (백엔드 findAll에서 포함) */
  enrolledStudents?: string[];
  monday: boolean;
  mondayStartTime: string | null;
  mondayEndTime: string | null;
  tuesday: boolean;
  tuesdayStartTime: string | null;
  tuesdayEndTime: string | null;
  wednesday: boolean;
  wednesdayStartTime: string | null;
  wednesdayEndTime: string | null;
  thursday: boolean;
  thursdayStartTime: string | null;
  thursdayEndTime: string | null;
  friday: boolean;
  fridayStartTime: string | null;
  fridayEndTime: string | null;
  saturday: boolean;
  saturdayStartTime: string | null;
  saturdayEndTime: string | null;
  sunday: boolean;
  sundayStartTime: string | null;
  sundayEndTime: string | null;
  [key: string]: any;
}

// ── 유틸리티 함수 ─────────────────────────────────────────────

/** dayIndex(0=월 … 6=일) → DB 컬럼 키 (예: "monday") */
export function getDayKey(dayIndex: number): string {
  return DAY_KEYS[dayIndex] ?? "";
}

/** "HH:MM:SS" 또는 "HH:MM" → 분 단위 정수 */
export function toMinutes(t: string): number {
  const parts = t.split(":").map(Number);
  return (parts[0] || 0) * 60 + (parts[1] || 0);
}

/**
 * 같은 교사의 같은 요일 수업들 중 cls의 순서(1-based)를 반환.
 * ClassBox의 좌/우 교차 배치에 사용됩니다.
 */
export function getTeacherDayOrder(
  cls: ClassData,
  dayIndex: number,
  allClasses: ClassData[]
): number {
  const dayKey = getDayKey(dayIndex);
  const startKey = `${dayKey}StartTime`;
  const endKey = `${dayKey}EndTime`;

  const sameTeacherDay = allClasses.filter(
    (c) =>
      c.teacher === cls.teacher &&
      Boolean(c[dayKey]) &&
      typeof c[startKey] === "string" &&
      typeof c[endKey] === "string"
  );

  sameTeacherDay.sort(
    (a, b) =>
      toMinutes(a[startKey] as string) - toMinutes(b[startKey] as string)
  );

  const idx = sameTeacherDay.findIndex((c) => c.id === cls.id);
  return idx >= 0 ? idx + 1 : 0;
}

/**
 * 수업 목록에서 표시할 시간 범위를 계산합니다.
 * 수업이 없으면 config.startHour 기준 1시간을 반환합니다.
 */
export function computeHourRange(
  classes: ClassData[],
  config: ScheduleConfig
): { minHour: number; maxHour: number } {
  let minHour = config.endHour;
  let maxHour = config.startHour;

  classes.forEach((cls) => {
    DAY_KEYS.forEach((dayKey) => {
      if (!cls[dayKey]) return;
      const startTime = cls[`${dayKey}StartTime`] as string | null;
      const endTime = cls[`${dayKey}EndTime`] as string | null;
      if (startTime) {
        const h = parseInt(startTime.split(":")[0]);
        if (h < minHour) minHour = h;
      }
      if (endTime) {
        const [h, m] = endTime.split(":").map(Number);
        const ceiled = m > 0 ? h + 1 : h;
        if (ceiled > maxHour) maxHour = ceiled;
      }
    });
  });

  if (minHour >= maxHour) {
    return { minHour: config.startHour, maxHour: config.startHour + 1 };
  }
  return { minHour, maxHour };
}
